import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSendComment } from 'infrastructure/hooks/comments/use-send';
import { useSendCcmPatientCall } from 'infrastructure/hooks/use-send-ccm-patient-call';
import { CommentType, ModuleType } from 'infrastructure/enums';

import type { FC, ReactNode } from 'react';

const CHANNEL_NAME = 'multi-patient-call';
const STORAGE_KEY = 'multiPatientCall';

type BroadcastMessage =
  | {
      type: 'CALL_STARTED';
      callId: string;
      patientGuid: string;
      isRpmCall: boolean;
    }
  | { type: 'CALL_ENDED' }
  | { type: 'PATIENT_REGISTERED'; patientGuid: string };

interface CallState {
  callId: string;
  isRpmCall: boolean;
  trackedPatients: string[];
}

interface IMultiPatientCallContext {
  startCall: (callId: string, patientGuid: string, isRpmCall: boolean) => void;
  endCall: () => void;
  onPatientVisited: (patientGuid: string) => void;
  showPrompt: boolean;
  confirmMultiPatient: () => Promise<void>;
  rejectMultiPatient: () => void;
}

const MultiPatientCallContext = createContext<
  IMultiPatientCallContext | undefined
>(undefined);

export const useMultiPatientCall = () => {
  const ctx = useContext(MultiPatientCallContext);
  if (!ctx)
    throw new Error(
      'useMultiPatientCall must be used within MultiPatientCallProvider',
    );
  return ctx;
};

export const MultiPatientCallProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);

  const callStateRef = useRef<CallState | null>(null);
  const pendingPatientRef = useRef<string | null>(null);
  const isRegisteringRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const { sendComment } = useSendComment();
  const { sendInfo } = useSendCcmPatientCall();

  const persistState = (state: CallState | null) => {
    if (state) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  };

  const broadcast = useCallback((msg: BroadcastMessage) => {
    channelRef.current?.postMessage(msg);
  }, []);

  const applyCallStarted = useCallback(
    (callId: string, patientGuid: string, isRpmCall: boolean) => {
      callStateRef.current = {
        callId,
        isRpmCall,
        trackedPatients: [patientGuid],
      };
      persistState(callStateRef.current);
    },
    [],
  );

  const applyCallEnded = useCallback(() => {
    callStateRef.current = null;
    pendingPatientRef.current = null;
    isRegisteringRef.current = false;
    persistState(null);
    setShowPrompt(false);
  }, []);

  const applyPatientRegistered = useCallback((patientGuid: string) => {
    if (!callStateRef.current) return;
    if (!callStateRef.current.trackedPatients.includes(patientGuid)) {
      callStateRef.current.trackedPatients.push(patientGuid);
      persistState(callStateRef.current);
    }
  }, []);

  const registerPatient = useCallback(
    async (patientGuid: string) => {
      const state = callStateRef.current;
      if (!state || isRegisteringRef.current) return;

      isRegisteringRef.current = true;
      try {
        if (state.isRpmCall) {
          await sendComment(
            {
              commentType: ModuleType.RPM,
              message: state.callId,
              type: CommentType.Call,
              patientGuid,
            },
            { showResult: false, withTimeout: false },
          );
        } else {
          await sendInfo({ callId: state.callId, patientGuid });
        }
        applyPatientRegistered(patientGuid);
        broadcast({ type: 'PATIENT_REGISTERED', patientGuid });
      } finally {
        isRegisteringRef.current = false;
      }
    },
    [sendComment, sendInfo, applyPatientRegistered, broadcast],
  );

  const startCall = useCallback(
    (callId: string, patientGuid: string, isRpmCall: boolean) => {
      applyCallStarted(callId, patientGuid, isRpmCall);
      broadcast({ type: 'CALL_STARTED', callId, patientGuid, isRpmCall });
    },
    [applyCallStarted, broadcast],
  );

  const endCall = useCallback(() => {
    applyCallEnded();
    broadcast({ type: 'CALL_ENDED' });
  }, [applyCallEnded, broadcast]);

  const onPatientVisited = useCallback((patientGuid: string) => {
    const state = callStateRef.current;
    if (!state) return;
    if (state.trackedPatients.includes(patientGuid)) return;
    pendingPatientRef.current = patientGuid;
    setShowPrompt(true);
  }, []);

  const confirmMultiPatient = useCallback(async () => {
    const patientGuid = pendingPatientRef.current;
    setShowPrompt(false);
    pendingPatientRef.current = null;
    if (patientGuid) await registerPatient(patientGuid);
  }, [registerPatient]);

  const rejectMultiPatient = useCallback(() => {
    setShowPrompt(false);
    pendingPatientRef.current = null;
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (
          parsed &&
          typeof parsed.callId === 'string' &&
          typeof parsed.isRpmCall === 'boolean' &&
          Array.isArray(parsed.trackedPatients)
        ) {
          callStateRef.current = parsed as CallState;
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }

    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (e: MessageEvent<BroadcastMessage>) => {
      const msg = e.data;
      if (msg.type === 'CALL_STARTED') {
        applyCallStarted(msg.callId, msg.patientGuid, msg.isRpmCall);
      } else if (msg.type === 'CALL_ENDED') {
        applyCallEnded();
      } else if (msg.type === 'PATIENT_REGISTERED') {
        applyPatientRegistered(msg.patientGuid);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [applyCallStarted, applyCallEnded, applyPatientRegistered]);

  return (
    <MultiPatientCallContext.Provider
      value={{
        startCall,
        endCall,
        onPatientVisited,
        showPrompt,
        confirmMultiPatient,
        rejectMultiPatient,
      }}
    >
      {children}
    </MultiPatientCallContext.Provider>
  );
};
